import { faUserCircle } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FastField, Form, Formik } from 'formik'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { useOutletContext, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import endUsersService from '../../services/endUsersService'
import { EndUser } from '../../types/end-user'
import { queryClient } from '../../utils/react-query'
import Card from '../Card'
import FormikInput from '../FormikInput'
import { BuzzlUsersError } from '../../types/buzzl-user'

const schema = Yup.object().shape({
  nbPoints: Yup.number(),
  nbPointsToGive: Yup.number().moreThan(0).required(),
})

export type AddPointsDTO = {
  nbPoints: number
  nbPointsToGive: number
}

const Details = () => {
  const { id } = useParams<{ id: string }>()
  const { nbPoints, user, nbSharedExperiences, location, isSuspended } = useOutletContext<EndUser>()

  const updateEndUser = useMutation(endUsersService.updateNumberNbPoints, {
    onSuccess: async () => {
      queryClient.refetchQueries('endUser')
      toast.success('Number of points updated.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const initialValues = {
    nbPoints: nbPoints || 0,
    nbPointsToGive: 0,
  }

  const handleOnSubmit = async (values: AddPointsDTO) => {
    try {
      if (id) {
        await updateEndUser.mutateAsync({ id, nbPoints: values.nbPointsToGive })
      }
    } catch (e) {
      console.log('🚀 ~ file: Details.tsx ~ line 37 ~ handleOnSubmit ~ e', e)
    }
  }

  return (
    <div>
      <Card.Container>
        <Card.Heading>
          <Card.HeadingLeft>
            <div className="flex-shrink-0">
              {user.profilePicture ? (
                <img
                  className="h-12 w-12 rounded-full"
                  src={user.profilePicture.url}
                  alt={user.profilePicture.altText}
                />
              ) : (
                <FontAwesomeIcon className="leading-icon" icon={faUserCircle} />
              )}
            </div>
            <div className="ml-4">
              <h3 className="card-title">{user.displayName}</h3>
            </div>
          </Card.HeadingLeft>
          <Card.HeadingRight>
            {isSuspended ? (
              <span className="tag alert-tag">Inactive</span>
            ) : (
              <span className="tag positive-tag">Active</span>
            )}
          </Card.HeadingRight>
        </Card.Heading>

        <Card.Field>
          <Card.Dt>Username</Card.Dt>
          <Card.Dd>{user.displayName}</Card.Dd>
        </Card.Field>

        <Card.Field>
          <Card.Dt>Email</Card.Dt>
          <Card.Dd>{user.email}</Card.Dd>
        </Card.Field>

        <Card.Field>
          <Card.Dt>Exeriences Shared</Card.Dt>
          <Card.Dd>{nbSharedExperiences}</Card.Dd>
        </Card.Field>

        {location && (
          <>
            <Card.Field>
              <Card.Dt>Area</Card.Dt>
              <Card.Dd>{location.city}</Card.Dd>
            </Card.Field>

            <Card.Field>
              <Card.Dt>Postcode</Card.Dt>
              <Card.Dd>{location.postcode || '-'}</Card.Dd>
            </Card.Field>
          </>
        )}
      </Card.Container>

      <Card.Container>
        <Card.Field col>
          <h3 className="card-title">Buzzjar Points</h3>
          <p className="mb-5 text-sm font-normal leading-5 text-gray-500">
            Update number of Buzzjar points this user has.
          </p>

          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={schema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleOnSubmit}
          >
            <Form className="flex items-start gap-5">
              <fieldset className="flex-1">
                <FastField
                  type="number"
                  component={FormikInput}
                  label="Current Number of Points"
                  placeholder="0"
                  name="nbPoints"
                  disabled
                />
              </fieldset>
              <fieldset className="flex-1">
                <FastField
                  type="number"
                  name="nbPointsToGive"
                  component={FormikInput}
                  label="New Number of Points"
                  placeholder="0"
                />
              </fieldset>

              <button className="btn btn-primary self-end" type="submit">
                Update
              </button>
            </Form>
          </Formik>
        </Card.Field>
      </Card.Container>
    </div>
  )
}

export default Details
